export type OrderStatusIconKey =
	| "message"
	| "clock"
	| "loader"
	| "check"
	| "wrench"
	| "shield"
	| "x"
	| "wallet"
	| "alert";

type OrderStatusVisual = {
	label: string;
	iconKey: OrderStatusIconKey;
	containerClassName: string;
	iconClassName: string;
	textClassName: string;
};

const DEFAULT_VISUAL: OrderStatusVisual = {
	label: "Desconocido",
	iconKey: "alert",
	containerClassName: "border border-gray-300 bg-gray-100",
	iconClassName: "text-gray-800",
	textClassName: "text-gray-800",
};

const ORDER_STATUS_VISUALS: Record<string, OrderStatusVisual> = {
	dialogando: {
		label: "Dialogando",
		iconKey: "message",
		containerClassName: "border border-[#8313C8] bg-[#ECD4FB]",
		iconClassName: "text-[#8313C8]",
		textClassName: "text-[#8313C8]",
	},
	en_espera: {
		label: "En Espera",
		iconKey: "clock",
		containerClassName: "border border-[#1447E6] bg-[#DBEAFE]",
		iconClassName: "text-[#1447E6]",
		textClassName: "text-[#1447E6]",
	},
	en_progreso: {
		label: "En Progreso",
		iconKey: "loader",
		containerClassName: "border border-[#008235] bg-[#DCFCE7]",
		iconClassName: "text-[#008235]",
		textClassName: "text-[#008235]",
	},
	completado: {
		label: "Completado",
		iconKey: "check",
		containerClassName: "border border-[#008235] bg-[#DCFCE7]",
		iconClassName: "text-[#008235]",
		textClassName: "text-[#008235]",
	},
	soporte: {
		label: "Soporte",
		iconKey: "wrench",
		containerClassName: "border border-[#1447E6] bg-[#DBEAFE]",
		iconClassName: "text-[#1447E6]",
		textClassName: "text-[#1447E6]",
	},
	garantia: {
		label: "Garantía",
		iconKey: "shield",
		containerClassName: "border border-[#894C00] bg-[#FEFCE8]",
		iconClassName: "text-[#894C00]",
		textClassName: "text-[#894C00]",
	},
	cancelado: {
		label: "Cancelado",
		iconKey: "x",
		containerClassName: "border border-[#E93A1F] bg-[#FFD4CE]",
		iconClassName: "text-[#E93A1F]",
		textClassName: "text-[#E93A1F]",
	},
	pagado: {
		label: "Pagado",
		iconKey: "wallet",
		containerClassName: "border border-[#008235] bg-[#DCFCE7]",
		iconClassName: "text-[#008235]",
		textClassName: "text-[#008235]",
	},
	por_pagar: {
		label: "Por Pagar",
		iconKey: "wallet",
		containerClassName: "border border-[#008235] bg-[#DCFCE7]",
		iconClassName: "text-[#008235]",
		textClassName: "text-[#008235]",
	},
};

function toOrderStatusKey(raw?: string | null): string {
	if (!raw) return "";
	return String(raw)
		.trim()
		.toLowerCase()
		.replace(/[\s_]+/g, "_");
}

function toTitleCase(value: string): string {
	return value
		.toLowerCase()
		.split(" ")
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

export function normalizeOrderStatusLabel(raw?: string | null): string {
	const key = toOrderStatusKey(raw);
	const known = ORDER_STATUS_VISUALS[key];
	if (known) return known.label;
	if (!raw) return DEFAULT_VISUAL.label;
	const compact = String(raw)
		.trim()
		.replace(/[_\s]+/g, " ");
	return compact ? toTitleCase(compact) : DEFAULT_VISUAL.label;
}

export function getOrderStatusVisual(raw?: string | null): OrderStatusVisual {
	const key = toOrderStatusKey(raw);
	const known = ORDER_STATUS_VISUALS[key];
	if (known) return known;
	return {
		...DEFAULT_VISUAL,
		label: normalizeOrderStatusLabel(raw),
	};
}

export function getOrderStatusChangeCopy(payload: {
	newStatus?: string | null;
	previousStatus?: string | null;
	fallbackMessage?: string | null;
}) {
	const nextLabel = normalizeOrderStatusLabel(payload.newStatus);
	const prevLabel = normalizeOrderStatusLabel(payload.previousStatus);
	const hasPrevious = Boolean(
		payload.previousStatus && String(payload.previousStatus).trim(),
	);
	const hasNext = Boolean(payload.newStatus && String(payload.newStatus).trim());
	const fallback = String(payload.fallbackMessage ?? "").trim();

	const message = hasNext
		? hasPrevious
			? `El estado del pedido cambió de ${prevLabel} a ${nextLabel}.`
			: `El estado del pedido ahora es ${nextLabel}.`
		: fallback || "El estado del pedido ha sido actualizado.";

	const preview = hasNext
		? `Pedido actualizado: ${nextLabel}`
		: "Estado del pedido actualizado";

	return {
		message,
		preview,
		visual: getOrderStatusVisual(payload.newStatus),
	};
}
